import { bound } from "@frui.ts/helpers";
import { watchBusy } from "@frui.ts/screens";
import type { EntityNames } from "config";
import { Modules } from "config";
import BankCodeType from "entities/bankCodeType";
import FeeType from "entities/feeType";
import RefundPaymentType from "entities/refundPaymentType";
import type RejectedCustomerRefundCandidateDetailResponse from "entities/rejectedCustomerRefundCandidateDetailResponse";
import type { interfaces } from "inversify";
import { computed } from "mobx";
import RejectedCustomerRefundCandidatesRepository from "repositories/rejectedCustomerRefundCandidatesRepository";
import ConfirmationService from "services/confirmationService";
import EnumService from "services/enumService";
import EventBus, { rejectedCustomerRefundCandidateRecreated } from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import OptionsService from "services/optionsService";
import SecurityService from "services/securityService";
import AlertsViewModel from "viewModels/alertsViewModel";
import DetailViewModel from "viewModels/detailViewModel";
import type PagingManagerViewModel from "viewModels/pagingManagerViewModel";
import CountrySelectViewModel from "../../../countrySelectViewModel";
import EnumRepository from "repositories/enumRepository";
import PriceListService from "services/priceListService";
import { emptyValue } from "utils/emptyValue";
import { translateChargeTypeByPriceListItem } from "utils/chargeType";

export default class RejectedCustomerRefundCandidateDetailViewModel extends DetailViewModel<RejectedCustomerRefundCandidateDetailResponse> {
  readonly modelName = "rejectedCustomerRefundCandidate";
  readonly countrySelectViewModel: CountrySelectViewModel;

  @computed
  get name() {
    return this.translate("detail");
  }

  @computed
  get allowToRecreate() {
    return this.securityService.allowTo("finance", "write") || this.securityService.allowTo("callcenter.finance", "write");
  }

  @computed
  get hasNext() {
    return this.pagingManager.hasNext;
  }

  @computed
  get hasPrevious() {
    return this.pagingManager.hasPrevious;
  }

  @computed
  get chargeRefundFee() {
    return (this.item.fees ?? []).find(fee => fee.feeType === FeeType.ChargeRefundFee)?.amount ?? 0;
  }

  @computed
  get bankFees() {
    return (this.item.fees ?? []).reduce((sum: number, fee) => {
      if (fee.feeType === FeeType.ForeignPaymentCost || fee.feeType === FeeType.ForeignPaymentToSepaCountriesCost) {
        sum += fee.amount;
      }
      return sum;
    }, 0);
  }

  @computed
  get isSEPAPayment() {
    return this.item.refundPayment.paymentType === RefundPaymentType.SepaTransfer;
  }

  @computed
  get showSEPAWarning() {
    return false;
  }

  get isPaymentEditable() {
    return false;
  }

  @computed
  get isPayGateRefund() {
    return this.item.refundPayment.paymentType === RefundPaymentType.PayGate;
  }

  @computed
  get isUserAllowedToEdit() {
    return true;
  }

  @computed
  get transferTypeOptions() {
    return this.optionsService.internalEnumOptions(RefundPaymentType).filter(item => item.id !== RefundPaymentType.PayGate);
  }

  @computed
  get bankCodeOptions() {
    return this.optionsService.internalEnumOptions(BankCodeType);
  }

  constructor(
    protected itemId: string,
    private pagingManager: PagingManagerViewModel,
    protected localizationService: LocalizationService,
    protected securityService: SecurityService,
    public alertsViewModel: AlertsViewModel,
    private enumService: EnumService,
    private confirmationService: ConfirmationService,
    private eventBus: EventBus,
    private optionsService: OptionsService,
    private notificationService: NotificationService,
    private repository: RejectedCustomerRefundCandidatesRepository,
    private enumRepository: EnumRepository,
    private priceListService: PriceListService,
    private countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>
  ) {
    super(itemId);
    this.countrySelectViewModel = this.countrySelectViewModelFactory("id");
  }

  async onInitialize(): Promise<void> {
    await super.onInitialize();
  }

  protected loadDetail() {
    return this.repository.get(this.itemId);
  }

  @bound
  @watchBusy
  async onNextClick() {
    const nextItemId = await this.pagingManager.onNextClick();

    if (nextItemId) {
      this.itemId = nextItemId;
      this.navigationName = `${nextItemId}`;
      this.notifyNavigationChanged();

      await this.onInitialize();
    }
  }

  @bound
  @watchBusy
  async onPreviousClick() {
    const prevItemId = await this.pagingManager.onPreviousClick();

    if (prevItemId) {
      this.itemId = prevItemId;
      this.navigationName = `${prevItemId}`;
      this.notifyNavigationChanged();

      await this.onInitialize();
    }
  }

  @bound
  async onRecreateClick() {
    if (!this.item.permittedActions.recreation.isAllowed) {
      return;
    }

    try {
      const confirmed = await this.confirmationService.showConfirmation(this.translate("recreationConfirmation.message"), {
        title: this.translate("recreationConfirmation.title"),
        confirmButton: { variant: "primary", text: this.translate("recreationConfirmation.confirm") },
      });

      if (confirmed) {
        await this.repository.recreations(this.item.id);
        this.eventBus.publish(rejectedCustomerRefundCandidateRecreated({ rejectedCustomerRefundCandidate: this.item }));
        this.notificationService.add({ title: this.translate("candidateRecreated"), type: NotificationType.SUCCESS });
        this.requestClose();
      }
    } catch (e) {
      // Silent catch
    }
  }

  @bound
  getPriceListPrice(priceListId: string) {
    return this.priceListService.chargePriceList.find(x => x.id === priceListId)?.price ?? 0;
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.finance, `rejectedCustomerRefundCandidates.${code}`, size);
  }

  translateSubAttribute(model: EntityNames | string, attribute: string) {
    return this.localizationService.translateAttribute(model, attribute);
  }

  translateChargeType(priceListId: string) {
    return translateChargeTypeByPriceListItem(this.priceListService, this.enumService, priceListId);
  }

  translateFuelType(priceListId: string) {
    const fuelTypeId = this.priceListService.chargePriceList.find(x => x.id === priceListId)?.fuelTypeId;

    if (!fuelTypeId) {
      return emptyValue;
    }

    return this.enumService.value("fuel_types", fuelTypeId)?.name ?? "";
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string, pagingManager: PagingManagerViewModel<any>) =>
      new RejectedCustomerRefundCandidateDetailViewModel(
        itemId,
        pagingManager,
        container.get(LocalizationService),
        container.get(SecurityService),
        container.get(AlertsViewModel),
        container.get(EnumService),
        container.get(ConfirmationService),
        container.get(EventBus),
        container.get(OptionsService),
        container.get(NotificationService),
        container.get(RejectedCustomerRefundCandidatesRepository),
        container.get(EnumRepository),
        container.get(PriceListService),
        container.get(CountrySelectViewModel.Factory)
      );
  }
}
